import { createRootRoute, Outlet } from '@tanstack/react-router';
import { lazy, Suspense } from 'react';
import { getPreferredLocale } from '@shared/config/i18n';
import { useLocale } from '@shared/config/router';
import { ErrorPage, NotFoundPage } from '@pages/error';
import { useMediaQuery } from '@shared/lib/hooks';
import { Toaster } from '@shared/ui';
import { MoveHelpdeskWidget } from '@app/providers/MoveHelpdeskWidget';

/**
 * Root Route - Global layout wrapper
 *
 * Provides:
 * - Global page structure (min-h-screen)
 * - Error boundary for route errors
 * - 404 page for unknown routes
 * - Router DevTools (development only)
 *
 * Note: scrollRestoration is configured in RouterProvider.tsx.
 * Note: AuthProvider wraps the router in main.tsx (above route tree)
 *
 * @updated DOC-0074 (2025-12-19) - Moved AuthProvider to main.tsx for router context
 * @updated DOC-0099 (2025-12-26) - Use pages from FSD pages layer
 */

// Lazy load devtools in development only
const TanStackRouterDevtools = import.meta.env.DEV
  ? lazy(() =>
      import('@tanstack/react-router-devtools').then((res) => ({
        default: res.TanStackRouterDevtools,
      }))
    )
  : () => null;

export const Route = createRootRoute({
  component: RootLayout,
  errorComponent: RootErrorBoundary,
  notFoundComponent: RootNotFoundPage,
});

function RootLayout() {
  const showDevtools = useMediaQuery('(min-width: 360px)');

  return (
    <div className="min-h-screen bg-background font-sans text-foreground">
      <Outlet />
      <MoveHelpdeskWidget />
      <Toaster />
      {import.meta.env.DEV && showDevtools && (
        <Suspense>
          <TanStackRouterDevtools position="bottom-right" />
        </Suspense>
      )}
    </div>
  );
}

/**
 * Root Error Boundary
 * Catches and displays errors that occur during route rendering
 */
function RootErrorBoundary({ error }: { error: Error }) {
  const locale = useLocale() ?? getPreferredLocale();
  return <ErrorPage error={error} locale={locale} />;
}

/**
 * 404 Not Found Page
 * Displayed when a route doesn't match any defined routes
 */
function RootNotFoundPage() {
  const locale = useLocale() ?? getPreferredLocale();
  return <NotFoundPage locale={locale} />;
}
